import React, { CSSProperties, memo } from 'react';
import cn from 'classnames';

import { Icons } from '@icons/icons';

import { useDownloadFile } from '@hooks/common/useDownloadFile';

import { getAvatarColor } from '@utils/common';
import { generateLogoInitials } from '@utils/generateLogoInitials';

import styles from './TextAvatar.module.scss';

interface ITextAvatarProps {
	firstName?: string;
	lastName?: string;
	fullName?: string;
	id?: string | number;
	isProfileLogo?: boolean;
	isUnknown?: boolean;
	size: string;
	fontSize: string;
	fontWeight?: number;
	onClick?: () => void;
	className?: string;
	avatarId?: string;
	avatarColor?: string;
	avatarStyles?: CSSProperties;
	isLogoSquare?: boolean;
}

const TextAvatar = ({
	firstName,
	lastName,
	fullName,
	id,
	isProfileLogo = false,
	isUnknown = false,
	size,
	fontSize,
	fontWeight = 500,
	onClick,
	className,
	avatarId = '',
	avatarColor,
	isLogoSquare,
	avatarStyles = {},
}: ITextAvatarProps) => {
	const { downloadUrl } = useDownloadFile({ id: avatarId });

	const logoInitials = generateLogoInitials((fullName || firstName) as string, lastName);

	const background = downloadUrl
		? { backgroundImage: `url(${downloadUrl})` }
		: { background: avatarColor || getAvatarColor(logoInitials, id) };

	const avatarClasses = cn(styles.avatarWrapper, className, {
		[styles.avatar]: !isProfileLogo,
		[styles.profileLogo]: isProfileLogo,
	});
	const imageClassname = cn(styles.image, {
		[styles.clickable]: !!onClick,
	});

	return (
		<div
			className={avatarClasses}
			style={{
				borderRadius: isLogoSquare ? '4px' : '50%',
				...avatarStyles,
			}}
		>
			<div
				className={imageClassname}
				onClick={onClick}
				style={{
					...background,
					width: size,
					height: size,
					fontSize,
					fontWeight,
					borderRadius: isLogoSquare ? '4px' : '50%',
				}}>
				{isUnknown
					? Icons.unknownLarge()
					: !downloadUrl && logoInitials
				}
			</div>
		</div>
	);
};

export default memo(TextAvatar);
